import React, { useState, useEffect } from "react";
import axios from "axios";
import Loader from "react-loader-spinner";
import styled from "styled-components";
import { cssVar, lighten } from "polished";

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin: 0.5rem 0;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: translateY(-2px);
  }
`;

const StatHeader = styled.div`
  margin-bottom: 1rem;

  h3 {
    margin: 0;
    color: var(--primaryTextColor);
    font-size: 1.1rem;
    font-weight: 600;
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--primaryColorDark);
  margin-bottom: 0.5rem;
`;

const StatDescription = styled.p`
  color: var(--greyText);
  margin: 0;
  font-size: 0.9rem;
`;

const DashboardWrapper = styled.div`
  padding: 2rem;
  background: ${() => lighten(0.01, "#eee")};
  border: ${`2px solid ${lighten(0.91, "#000")}`};
  border-radius: 5px;
`;

export default function Dashboard({ practiceId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [practiceId]);

  function fetchData() {
    setLoading(true);

    const req = {};

    if (practiceId) {
      req.practice_id = practiceId;
    }

    axios
      .post("/dashboard/", req)
      .then(({ data }) => {
        const res = data.response[0];
        setData({
          meta_blocks: [
            {
              title: "Patients",
              value: res.patients_count,
            },
            {
              title: "Committed Procedures",
              value: res.procedure_count,
            },
            {
              title: "Mean Age at Treatment",
              value: res.mean_patient_age_at_treatment,
            },
            {
              title: "Mean Pre-Op Meds",
              value: res.mean_procedure_meds,
            },
            {
              title: "Mean Pre-Op IOP",
              value: res.mean_procedure_iop,
            },
            {
              title: "Mean Post-Op IOP",
              value: res.mean_post_op_iop,
            },
            {
              title: "Mean Post-Op Meds",
              value: res.mean_post_op_meds,
            },
            {
              title: "Mean Follow Up",
              value: res.mean_visit_range,
            },
          ].map((m, idx) => ({
            ...m,
            key: `meta_blocks_${idx}`,
          })),
          glaucoma_counts: res.glaucoma_counts.map((g, idx) => ({
            ...g,
            key: `glaucoma_counts_${idx}`,
          })),
          treatment_related_complications:
            res.treatment_related_complications.map((t, idx) => ({
              ...t,
              key: `treatment_related_complications_${idx}`,
            })),
        });
        setLoading(false);
      })
      .catch((err) => {
        setData(null);
        setLoading(false);
      });
  }

  return (
    <DashboardWrapper>
      {loading && (
        <Loader type="ThreeDots" color="#000" height={15} width={15} />
      )}
      {data && (
        <div>
          <StatGrid>
            {data.meta_blocks.map(({ key, title, description, value }) => (
              <StatCard key={key}>
                <StatHeader>
                  <h3>{title}</h3>
                </StatHeader>
                <StatValue>{value}</StatValue>
                <StatDescription>{description}</StatDescription>
              </StatCard>
            ))}
          </StatGrid>
          <div style={{ marginTop: "30px" }}>
            <h3 style={{ marginBottom: "20px", color: "#000" }}>
              Glaucoma Diagnosis
            </h3>
            {data.glaucoma_counts.length > 0 ? (
              <StatGrid>
                {data.glaucoma_counts.map(({ key, name, count }) => (
                  <StatCard key={key}>
                    <StatHeader>
                      <h3>{name}</h3>
                    </StatHeader>
                    <StatValue>{count}</StatValue>
                  </StatCard>
                ))}
              </StatGrid>
            ) : (
              <div>
                <p>None to report</p>
              </div>
            )}
          </div>
          <div style={{ marginTop: "30px" }}>
            <h3 style={{ marginBottom: "20px", color: "#000" }}>
              Treatment Related Complications
            </h3>
            {data.treatment_related_complications.length > 0 ? (
              <StatGrid>
                {data.treatment_related_complications.map(
                  ({ key, name, count }) => (
                    <StatCard key={key}>
                      <StatHeader>
                        <h3>{name}</h3>
                      </StatHeader>
                      <StatValue>{count}</StatValue>
                    </StatCard>
                  )
                )}
              </StatGrid>
            ) : (
              <div>
                <p>None to report</p>
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardWrapper>
  );
}
